import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { clearCacheForKey } from '../utils/cache-utils';
import { saveLocalData } from '../utils/local-storage';
import { STORAGE_KEYS, STORAGE_KEY_TO_TABLE_MAP } from '../constants/storage-keys';
import { eventBus, SYNC_EVENTS } from '../utils';
import { 
  FastingEntry, 
  WalkingEntry, 
  JournalEntry, 
  FoodEntry, 
  HealthEntry, 
  WellnessEntry 
} from '../models/entry-types';
import { TableName, SupabaseTableName } from './types';
import { cleanupDeletedEntries } from './entry-operations';
import { saveSettingsToDatabase } from './settings-service';

export const saveData = async (key: string, data: any): Promise<void> => {
  try {
    if (import.meta.env.DEV) console.log(`Starting saveData for key: ${key}`);
    
    // Validate inputs
    if (!key) {
      console.error('Invalid key provided to saveData:', key);
      throw new Error('Invalid storage key');
    }
    
    if (!data) {
      console.error('No data provided to saveData');
      throw new Error('No data provided');
    }
    
    // Save locally first for responsiveness
    saveLocalData(key, data);
    clearCacheForKey(key);
    
    // Auth check
    if (import.meta.env.DEV) console.log('Checking authentication before saving to database');
    const { data: sessionData, error: sessionError } = await supabase.auth.getSession();
    
    if (sessionError) {
      console.error('Session error when saving data:', sessionError);
      throw new Error(`Authentication error: ${sessionError.message}`);
    }
    
    const userId = sessionData.session?.user?.id;

    if (!userId) {
      if (import.meta.env.DEV) console.log('User not authenticated, saving to localStorage only');
      return;
    }

    if (import.meta.env.DEV) console.log(`Saving data for key: ${key}`);
    
    const tableName = STORAGE_KEY_TO_TABLE_MAP[key];
    
    if (!tableName) {
      console.error(`No table mapping found for key: ${key}`);
      toast.error('Configuration error: Invalid storage key');
      return;
    }
    
    if (import.meta.env.DEV) console.log('Saving to table:', tableName);

    // Special case for settings, use the settings-specific save function
    if (tableName === 'user_settings') {
      await saveSettingsToDatabase(data);
      return;
    }

    // Ensure all items have the correct user_id
    const dataWithUserId = Array.isArray(data) 
      ? data.map(item => ({ ...item, user_id: userId }))
      : { ...data, user_id: userId };

    if (import.meta.env.DEV) console.log('Saving to database:', Array.isArray(dataWithUserId) ? `${dataWithUserId.length} items` : 'single item');

    // Convert entry data to database format if needed
    const preparedData = Array.isArray(dataWithUserId) 
      ? dataWithUserId.map(item => prepareDataForDatabase(item, tableName as TableName))
      : prepareDataForDatabase(dataWithUserId, tableName as TableName);

    // Use explicit type casting for tableName
    const typedTableName = tableName as SupabaseTableName;
    
    // Retry mechanism for database save
    const maxRetries = 2;
    let retries = 0;
    let success = false;
    
    while (!success && retries <= maxRetries) {
      try {
        if (retries > 0) {
          if (import.meta.env.DEV) console.log(`Retry attempt ${retries} for saving to ${tableName}`);
        }
        
        const { error } = await supabase
          .from(typedTableName)
          .upsert(preparedData);

        if (error) {
          console.error('Error saving data:', error);
          
          // Special handling for RLS policy violations
          if (error.message?.includes('Row level security') || error.code === '42501') {
            console.error('RLS policy violation');
            throw new Error('RLS Policy Violation');
          }
          
          // If not final retry, continue to next attempt
          if (retries < maxRetries) {
            retries++;
            // Add exponential backoff
            await new Promise(resolve => setTimeout(resolve, 1000 * retries));
            continue;
          }
          
          throw error;
        }

        if (import.meta.env.DEV) console.log(`Successfully saved data to ${tableName}`);
        success = true;
        eventBus.dispatch(SYNC_EVENTS.ALL_DATA_UPDATED);
      } catch (error: any) {
        console.error('Error in Supabase operation:', error);
        
        if (retries >= maxRetries) {
          throw error;  // Re-throw to be caught by the outer try-catch
        }
        
        retries++;
        await new Promise(resolve => setTimeout(resolve, 1000 * retries));
      }
    }
  } catch (error: any) {
    console.error('Error in saveData:', error);
    let errorMessage = 'Failed to save data. ';
    
    if (error.message?.includes('Row level security') || error.code === '42501') {
      errorMessage += 'Please try logging out and back in.';
    } else if (!navigator.onLine) {
      errorMessage += 'You appear to be offline.';
    }
    
    toast.error(errorMessage);
    throw error;
  }
};

// Helper function to prepare data for database format
function prepareDataForDatabase(item: any, tableName: TableName): any {
  // Clone the item to avoid modifying the original
  const result = { ...item };
  
  // Handle specific data transformations for each table type
  switch (tableName) {
    case 'food_entries':
      // Convert mealType to meal_type for food entries
      if ('mealType' in result) {
        result.meal_type = result.mealType;
        delete result.mealType;
      }
      
      // Remove 'type' field as it's not in the database schema
      if ('type' in result) {
        delete result.type;
      }
      break;
      
    case 'health_entries':
      // Convert bloodPressureSystolic to blood_pressure_systolic
      if ('bloodPressureSystolic' in result) {
        result.blood_pressure_systolic = result.bloodPressureSystolic;
        delete result.bloodPressureSystolic;
      }
      
      // Convert bloodPressureDiastolic to blood_pressure_diastolic
      if ('bloodPressureDiastolic' in result) {
        result.blood_pressure_diastolic = result.bloodPressureDiastolic;
        delete result.bloodPressureDiastolic;
      }
      
      // Remove 'type' field
      if ('type' in result) {
        delete result.type;
      }
      break;
      
    case 'fasting_sessions':
      // Convert startTime to start_time
      if ('startTime' in result) {
        result.start_time = result.startTime;
        delete result.startTime;
      }
      
      // Convert endTime to end_time
      if ('endTime' in result) {
        result.end_time = result.endTime;
        delete result.endTime;
      }
      
      // Remove 'type' field
      if ('type' in result) {
        delete result.type;
      }
      break;
      
    case 'active_fasting':
      // Convert startTime to start_time
      if ('startTime' in result) {
        result.start_time = result.startTime;
        delete result.startTime;
      }
      
      // Remove 'type' field if it exists
      if ('type' in result) {
        delete result.type;
      }
      break;
    case 'water_entries':
      // No field transformations needed for water entries
      // Just remove 'type' field as it's not in the database schema
      if ('type' in result) {
        delete result.type;
      }
      break;
        
    default:
      // For other tables, just remove the 'type' field if it exists
      if ('type' in result) {
        delete result.type;
      }
  }
  
  return result;
}
export { saveLocalData };

async function saveFastingSessions(sessions: FastingEntry[], userId: string) {
  const results = [];
  for (const session of sessions) {
    try {
      if (import.meta.env.DEV) console.log('Saving fasting session:', { id: session.id, userId });
      const { data, error } = await supabase.from('fasting_sessions').upsert({
        id: session.id,
        user_id: userId,
        start_time: session.startTime,
        end_time: session.endTime,
        duration: session.duration,
        timestamp: session.timestamp,
        date: session.date
      });
      
      if (error) {
        console.error('Error upserting fasting session:', error);
        throw error;
      }
      
      results.push({ id: session.id, success: true });
    } catch (error) {
      console.error(`Failed to save fasting session ${session.id}:`, error);
      results.push({ id: session.id, success: false, error });
    }
  }
  
  try {
    await cleanupDeletedEntries(sessions, userId, 'fasting_sessions');
  } catch (error) {
    console.error('Failed to clean up deleted fasting sessions:', error);
  }
  
  return results;
}

async function saveWalkingEntries(entries: WalkingEntry[], userId: string) {
  const results = [];
  for (const entry of entries) {
    try {
      if (import.meta.env.DEV) console.log('Saving walking entry:', { id: entry.id, userId, duration: entry.duration });
      const { data, error } = await supabase.from('walking_entries').upsert({
        id: entry.id,
        user_id: userId,
        duration: entry.duration,
        timestamp: entry.timestamp,
        date: entry.date
      });
      
      if (error) {
        console.error('Error upserting walking entry:', error);
        throw error;
      }
      
      results.push({ id: entry.id, success: true });
    } catch (error) {
      console.error(`Failed to save walking entry ${entry.id}:`, error);
      results.push({ id: entry.id, success: false, error });
    }
  }
  
  try {
    await cleanupDeletedEntries(entries, userId, 'walking_entries');
  } catch (error) {
    console.error('Failed to clean up deleted walking entries:', error);
  }
  
  return results;
}

async function saveJournalEntries(entries: JournalEntry[], userId: string) {
  const results = [];
  for (const entry of entries) {
    try {
      if (import.meta.env.DEV) console.log('Saving journal entry:', { id: entry.id, userId });
      const { data, error } = await supabase.from('journal_entries').upsert({
        id: entry.id,
        user_id: userId,
        content: entry.content,
        timestamp: entry.timestamp,
        date: entry.date
      });
      
      if (error) {
        console.error('Error upserting journal entry:', error);
        throw error;
      }
      
      results.push({ id: entry.id, success: true });
    } catch (error) {
      console.error(`Failed to save journal entry ${entry.id}:`, error);
      results.push({ id: entry.id, success: false, error });
    }
  }
  
  try {
    await cleanupDeletedEntries(entries, userId, 'journal_entries');
  } catch (error) {
    console.error('Failed to clean up deleted journal entries:', error);
  }
  
  return results;
}

async function saveFoodEntries(entries: FoodEntry[], userId: string) {
  const results = [];
  for (const entry of entries) {
    try {
      if (import.meta.env.DEV) console.log('Saving food entry:', { 
        id: entry.id, 
        userId, 
        mealType: entry.mealType, 
        calories: entry.calories,
        date: entry.date
      });
      
      const { data, error } = await supabase.from('food_entries').upsert({
        id: entry.id,
        user_id: userId,
        carbs: entry.carbs,
        protein: entry.protein,
        fat: entry.fat,
        fiber: entry.fiber,
        sugar: entry.sugar,
        calories: entry.calories,
        meal_type: entry.mealType,
        description: entry.description,
        timestamp: entry.timestamp,
        date: entry.date
      });
      
      if (error) {
        console.error('Error upserting food entry:', error);
        toast.error('Failed to save food entry. Please try again.');
        throw error;
      }
      
      if (import.meta.env.DEV) console.log('Food entry saved successfully:', entry.id);
      results.push({ id: entry.id, success: true });
    } catch (error) {
      console.error(`Failed to save food entry ${entry.id}:`, error);
      results.push({ id: entry.id, success: false, error });
    }
  }
  
  try {
    await cleanupDeletedEntries(entries, userId, 'food_entries');
  } catch (error) {
    console.error('Failed to clean up deleted food entries:', error);
  }
  
  return results;
}

async function saveHealthEntries(entries: HealthEntry[], userId: string) {
  const results = [];
  for (const entry of entries) {
    try {
      if (import.meta.env.DEV) console.log('Saving health entry:', { id: entry.id, userId });
      const { data, error } = await supabase.from('health_entries').upsert({
        id: entry.id,
        user_id: userId,
        weight: entry.weight,
        blood_pressure_systolic: entry.bloodPressureSystolic,
        blood_pressure_diastolic: entry.bloodPressureDiastolic,
        glucose: entry.glucose,
        sleep: entry.sleep,
        timestamp: entry.timestamp,
        date: entry.date
      });
      
      if (error) {
        console.error('Error upserting health entry:', error);
        throw error;
      }
      
      results.push({ id: entry.id, success: true });
    } catch (error) {
      console.error(`Failed to save health entry ${entry.id}:`, error);
      results.push({ id: entry.id, success: false, error });
    }
  }
  
  try {
    await cleanupDeletedEntries(entries, userId, 'health_entries');
  } catch (error) {
    console.error('Failed to clean up deleted health entries:', error);
  }
  
  return results;
}
